import { Alert, styled } from "@mui/material"
import type { Map as MapboxMap } from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import { useCallback, useRef, useState } from "react"
import type { LayerProps, MapRef } from "react-map-gl/mapbox"
import Map, { Layer, Source } from "react-map-gl/mapbox"
import { MAPBOX_ACCESS_TOKEN } from "../../utils/env"
import { NodeFormDialog } from "../Cop/NodeFormDialog"
import { BioMeshFilterPanel } from "./BioMeshFilterPanel"
import { useBioMeshUIState } from "./BioMeshUIStateContext"
import { EditModeToggle } from "./EditModeToggle"
import { ExportVisibleButton } from "./ExportVisibleButton"
import { generateHexGrid } from "./hexGrid"
import { LassoSelectToggle } from "./LassoSelectToggle"
import { DRAWER_WIDTH, NodeDrawerView } from "./NodeDrawerView"
import { NodeLayer } from "./NodeLayer"
import { ResizableMapContainer } from "./ResizableMapContainer"
import { SensorSummaryPanel } from "./SensorSummaryPanel"
import { useLassoSelect } from "./useLassoSelect"
import { useNodeClusters } from "./useNodeClusters"
import { useNodeDrawer } from "./useNodeDrawer"
import { ViewUnmappedNodesButton } from "./ViewUnmappedNodesButton"

/** Hex grid covering the full world. Adjust cellSize to change hex density. */
const hexGridData = generateHexGrid([-180, -80, 180, 80], 5)

const hexGridLineLayer: LayerProps = {
  id: "hex-grid",
  type: "line",
  paint: {
    "line-color": "#0ea5e9",
    "line-width": 0.5,
    "line-opacity": 0.15,
  },
  beforeId: "country-boundaries",
}

const lassoFillLayer: LayerProps = {
  id: "lasso-fill",
  type: "fill",
  paint: {
    "fill-color": "#37b2ff",
    "fill-opacity": 0.15,
  },
}

const lassoLineLayer: LayerProps = {
  id: "lasso-line",
  type: "line",
  paint: {
    "line-color": "#37b2ff",
    "line-width": 2,
    "line-dasharray": [3, 2],
  },
}

const StyledMap = styled(ResizableMapContainer)({
  width: "100%",
  height: "100%",
  ".mapboxgl-ctrl-bottom-right, .mapboxgl-ctrl-bottom-left": {
    bottom: 80,
  },
})

const PlacementAlert = styled(Alert)({
  position: "absolute",
  top: 52,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 2,
})

const ButtonBar = styled("div", {
  shouldForwardProp: prop => prop !== "drawerOpen",
})<{ drawerOpen: boolean }>(({ theme, drawerOpen }) => ({
  position: "absolute",
  top: 12,
  right: drawerOpen ? DRAWER_WIDTH + 56 : 56,
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  transition: theme.transitions.create("right", {
    duration: theme.transitions.duration.enteringScreen,
  }),
}))

type CreateNodeDialog =
  | { coords: { lat: number; lng: number } }
  | { unmapped: true }
  | null

export const BioMeshMap = () => {
  const [styleLoaded, setStyleLoaded] = useState(false)
  const [createDialog, setCreateDialog] = useState<CreateNodeDialog>(null)
  const mapRef = useRef<MapRef>(null)
  const {
    isEditing,
    setIsEditing,
    isSelecting,
    setIsSelecting,
    clearSelection,
  } = useBioMeshUIState()

  const drawer = useNodeDrawer()
  const { getClusters, getLeaves } = useNodeClusters()
  const { lassoGeoJSON, clearLasso } = useLassoSelect(
    mapRef,
    getClusters,
    getLeaves
  )

  const cursorMode =
    drawer.placingNodeId || isSelecting || isEditing ? "crosshair" : undefined

  const onMapLoad = useCallback((evt: { target: MapboxMap }) => {
    const map = evt.target
    setStyleLoaded(true)
    map.on("style.load", () => setStyleLoaded(true))
  }, [])

  const handleMapClick = useCallback(
    async (evt: { lngLat: { lng: number; lat: number } }) => {
      if (drawer.placingNodeId) {
        await drawer.placeNode(evt.lngLat)
        return
      }
      if (!isEditing) return
      setCreateDialog({ coords: { lat: evt.lngLat.lat, lng: evt.lngLat.lng } })
    },
    [isEditing, drawer]
  )

  const handleToggleSelect = useCallback(() => {
    if (isSelecting) {
      setIsSelecting(false)
      clearSelection()
      clearLasso()
    } else {
      setIsSelecting(true)
    }
  }, [isSelecting, setIsSelecting, clearSelection, clearLasso])

  return (
    <>
      <StyledMap mapRef={mapRef}>
        <Map
          ref={mapRef}
          initialViewState={{
            longitude: 155,
            latitude: 5,
            zoom: 2,
          }}
          onLoad={onMapLoad}
          onClick={evt => void handleMapClick(evt)}
          cursor={cursorMode}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          minZoom={1.5}
          mapStyle="mapbox://styles/thepublichealthco/cmmavy6eg00c801su7td45kh3"
          projection="mercator"
        >
          {styleLoaded && (
            <>
              <Source id="hex-grid" type="geojson" data={hexGridData}>
                <Layer {...hexGridLineLayer} />
              </Source>
              <NodeLayer onOpenDrawer={drawer.openNodes} />
              <SensorSummaryPanel />
              {lassoGeoJSON && (
                <Source id="lasso-polygon" type="geojson" data={lassoGeoJSON}>
                  <Layer {...lassoFillLayer} />
                  <Layer {...lassoLineLayer} />
                </Source>
              )}
            </>
          )}
        </Map>

        <BioMeshFilterPanel />

        {drawer.placingNodeName && (
          <PlacementAlert severity="info" onClose={drawer.cancelPlacing}>
            Click on the map to place <strong>{drawer.placingNodeName}</strong>
          </PlacementAlert>
        )}

        <EditModeToggle
          isEditing={isEditing}
          onToggle={() => setIsEditing(!isEditing)}
          drawerOpen={drawer.isOpen}
          drawerWidth={DRAWER_WIDTH}
        />

        <LassoSelectToggle
          isSelecting={isSelecting}
          onToggle={handleToggleSelect}
          drawerOpen={drawer.isOpen}
          drawerWidth={DRAWER_WIDTH}
        />

        <ButtonBar drawerOpen={drawer.isOpen}>
          <ViewUnmappedNodesButton
            active={drawer.isUnmapped}
            onToggle={drawer.toggleUnmapped}
          />
          <ExportVisibleButton clearLasso={clearLasso} />
        </ButtonBar>
      </StyledMap>

      <NodeDrawerView
        nodeIds={drawer.nodeIds}
        onClose={drawer.close}
        title={drawer.isUnmapped ? "Unmapped Nodes" : "Node Details"}
        unmapped={drawer.isUnmapped}
        onPlaceOnMap={drawer.startPlacing}
        onCreateUnmapped={
          drawer.isUnmapped
            ? () => setCreateDialog({ unmapped: true })
            : undefined
        }
      />

      {createDialog && (
        <NodeFormDialog
          open
          onClose={() => setCreateDialog(null)}
          {...("coords" in createDialog
            ? { initialCoords: createDialog.coords }
            : { defaultUnmapped: true })}
        />
      )}
    </>
  )
}
